/**
 * `Sheet` — the modal sheet. `docs/design-direction.md` §9.8.
 *
 * `--bg-panel`, guillotine-cut, full width, capped at 86% of the app height, with a
 * 1px `--border-strong` top edge that **draws in left→right** as the sheet rises: a
 * printed rule appearing, not a card lifting (§7.2 SHEET RISE). The scrim is
 * `--scrim-bg` **plus a halftone dot layer**, because an overlay in this system reads
 * printed, never frosted — `backdrop-filter` is banned outright (§5.5, §12.4).
 *
 * The header is 48px: the title in Display 800 caps at `--fs-20` flush left, an
 * optional mono count, a 12px chalk-dash grabber, and a **44px text `ЗАКРЫТЬ`**.
 * Never an icon-only × and never a trash can — actions in this app are words in
 * square stamps (§9.8, §12.21).
 *
 * Dialog semantics, all of them required by §9.8:
 *
 *  · `role="dialog"`, `aria-modal="true"`, `aria-labelledby` on the header title.
 *  · **Focus is trapped**: focus moves into the sheet on open, `Tab` from the last
 *    focusable returns to the first and `Shift+Tab` from the first goes to the last,
 *    and the element that was focused before the sheet opened gets focus back on
 *    close. jsdom does not move focus on `Tab` by itself — neither does a browser
 *    once the handler calls `preventDefault` — so the trap is explicit, not implied.
 *  · **`Escape` closes**, and so does Telegram's `BackButton`, which is the hardware
 *    back gesture on Android and the only affordance many users will look for
 *    (§10.1, spec.md §13.1). The back button is restored to whatever state the screen
 *    had it in, so a sheet opened from a nested screen does not steal its back arrow.
 *  · **Drag-to-dismiss lives on the header only** (§9.8), with `touch-action: none`
 *    so Telegram's own sheet-dismiss gesture cannot steal the drag (§7.3). The drag
 *    writes a custom property straight onto the element rather than going through
 *    React state: a pointer move must not schedule a render.
 *
 * Under `prefers-reduced-motion` the rise and the rule-draw collapse to a single 1ms
 * step (`motion.css`), which is why both keyframes animate *into* the resting style —
 * the sheet simply is where it belongs, with its rule drawn (§7.4).
 *
 * The sheet renders through a portal into `<body>`. It has to: a `position: fixed`
 * layer nested inside an ancestor that establishes a stacking context (`.halftone`
 * sets `isolation: isolate`) would be trapped under it, and the z-layer stack of §5.7
 * puts sheets at 40 with the grain overlay above at 60.
 */

import { useCallback, useEffect, useId, useRef } from 'react';
import type { JSX, PointerEvent as ReactPointerEvent, KeyboardEvent, ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { hasCyrillic } from '@bl/shared';

import { telegram } from '../../lib/telegram';

import styles from './primitives.module.css';
import { cx } from './Rule';

/** Everything focusable, in DOM order, that a trap has to cycle through. */
const FOCUSABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
  'select:not([disabled]), textarea:not([disabled]), iframe, object, embed, ' +
  '[contenteditable="true"], [tabindex]:not([tabindex="-1"])';

/**
 * Drag distance past which the release dismisses. `--bar` (64px) — one full bar, and
 * far enough that a scroll that starts on the header does not throw the sheet away.
 */
const DISMISS_PX = 64;

/** The close control's copy. §9.8 states it literally; the test asserts it. */
export const SHEET_CLOSE_LABEL = 'ЗАКРЫТЬ';

export interface SheetProps {
  /** Nothing is rendered — no portal, no scrim, no listeners — while this is false. */
  readonly open: boolean;
  /** Header title, set in Display 800 caps at `--fs-20`. */
  readonly title: string;
  /** Escape, the close control, the scrim and a completed drag all call this. */
  readonly onClose: () => void;
  /**
   * The mono count beside the title (`12 ТРЕКОВ`, `1:38:04`). Omitted entirely when
   * absent — a sheet never prints a placeholder for a number it does not have
   * (§12.6).
   */
  readonly count?: string | undefined;
  readonly children?: ReactNode;
  readonly className?: string | undefined;
  /**
   * Extra class on the scrolling body. A sheet whose content owns its own scroller —
   * SET LIST's clipped paper — uses this to take the scroll away from the panel; see
   * `setlist.module.css`.
   */
  readonly bodyClassName?: string | undefined;
  /** Drag distance in px past which a header drag dismisses. Default 64 (`--bar`). */
  readonly dismissAfterPx?: number;
}

export function Sheet({
  open,
  title,
  onClose,
  count,
  children,
  className,
  bodyClassName,
  dismissAfterPx = DISMISS_PX,
}: SheetProps): JSX.Element | null {
  const titleId = useId();
  const sheetRef = useRef<HTMLElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const dragRef = useRef<{ startY: number; delta: number } | null>(null);

  /* ── focus: move in on open, put it back on close ─────────────────────────── */

  useEffect(() => {
    if (!open) return;

    const previous = document.activeElement;
    restoreRef.current = previous instanceof HTMLElement ? previous : null;

    /* The dialog itself takes focus, never the first control inside it: focusing
       `ЗАКРЫТЬ` would paint the §5.4 ring around it the moment the sheet rises. */
    sheetRef.current?.focus();

    return () => {
      restoreRef.current?.focus();
      restoreRef.current = null;
    };
  }, [open]);

  /* ── Telegram's BackButton closes the sheet, and is restored afterwards ───── */

  useEffect(() => {
    if (!open) return;

    const wasVisible = telegram.backButton.isVisible;
    const off = telegram.backButton.onClick(onClose);
    telegram.backButton.show();

    return () => {
      off();
      if (!wasVisible) telegram.backButton.hide();
    };
  }, [open, onClose]);

  /* ── keyboard: Escape closes, Tab cycles inside ───────────────────────────── */

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const sheet = sheetRef.current;
      if (sheet === null) return;

      const focusable = Array.from(sheet.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) {
        event.preventDefault();
        sheet.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) return;

      const active = document.activeElement;
      if (event.shiftKey) {
        if (active === first || active === sheet || !sheet.contains(active)) {
          event.preventDefault();
          last.focus();
        }
        return;
      }
      if (active === last || active === sheet || !sheet.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  /* ── header drag-to-dismiss ───────────────────────────────────────────────── */

  const setDrag = useCallback((px: number): void => {
    sheetRef.current?.style.setProperty('--sheet-drag', `${px}px`);
  }, []);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>): void => {
      /*
       * A press that starts on a control belongs to that control, not to the drag.
       * This is not a nicety: `setPointerCapture` on the header RETARGETS the whole
       * sequence to the header, so `pointerup` — and with it `click` — is delivered
       * to the header and the button's own `onClick` never runs. That is exactly how
       * `ЗАКРЫТЬ` went dead while looking perfectly pressable.
       */
      if (event.target instanceof Element && event.target.closest(FOCUSABLE) !== null) {
        return;
      }

      dragRef.current = { startY: event.clientY, delta: 0 };
      /* Not in jsdom, and absent on very old webviews; the move handler is attached
         to the same element either way, so capture is an improvement, not a
         requirement. */
      event.currentTarget.setPointerCapture?.(event.pointerId);
      setDrag(0);
    },
    [setDrag],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>): void => {
      const drag = dragRef.current;
      if (drag === null) return;
      /* Downwards only: an upward drag on the header is not a dismissal, and letting
         the sheet travel up would uncover the ground beneath it. */
      drag.delta = Math.max(0, event.clientY - drag.startY);
      setDrag(drag.delta);
    },
    [setDrag],
  );

  const endDrag = useCallback((): void => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (drag === null) return;
    setDrag(0);
    if (drag.delta >= dismissAfterPx) onClose();
  }, [dismissAfterPx, onClose, setDrag]);

  if (!open) return null;

  const hasCount = typeof count === 'string' && count.trim() !== '';

  return createPortal(
    <div className={styles.layer} data-sheet-layer="true">
      {/* The scrim is a printed halftone field over the scrim ink, and it is inert to
          assistive technology: the dialog is the content, and closing by tapping it
          is a pointer shortcut for Escape. */}
      <div className={cx('halftone', styles.scrim)} aria-hidden="true" onPointerDown={onClose} />

      <section
        ref={sheetRef}
        className={cx(styles.sheet, className)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <span className={styles.edge} aria-hidden="true" />

        <header
          className={styles.header}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <span className={styles.grabber} aria-hidden="true" />
          <h2 id={titleId} className={cx('t-display', 't-20', styles.title)}>
            {title}
          </h2>
          {hasCount ? (
            <span className={cx('t-mono', 't-11', styles.count, hasCyrillic(count) && styles.cyr)}>
              {count}
            </span>
          ) : null}
          <button
            type="button"
            className={cx('t-ui', 't-17', styles.close, styles.hit)}
            onClick={onClose}
          >
            {SHEET_CLOSE_LABEL}
          </button>
        </header>

        <div className={cx(styles.body, bodyClassName)}>{children}</div>
      </section>
    </div>,
    document.body,
  );
}
